import { createRouter, createWebHistory } from 'vue-router'
import type { RouteMeta } from 'vue-router'
import MainLayout from '@/layouts/MainLayout.vue'
import Login from '@/pages/Login.vue'
import Dashboard from '@/pages/Dashboard.vue'
import Restaurants from '@/pages/Restaurants.vue'
import { hasPermission, isDatevAccountant } from '@/utils/authorization'
import { useRestaurantInfoStore } from '@/stores/restaurantInfo'
import { useAuthStore } from '@/stores/auth'
import { config } from '@/config'
import { installChunkReload } from './chunkReload'

/**
 * Extend Vue Router's RouteMeta to support permission-based access control and role-based routing
 */
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiredPermissions?: string[]
    allowedRoles?: string[]
  }
}

const routes = [
  {
    path: '/login',
    component: Login,
    meta: { requiresAuth: false },
  },
  {
    path: '/signup',
    component: () => import('@/pages/Signup.vue'),
    meta: { requiresAuth: false },
  },
  {
    // Legacy /register/:slug? — redirect to /signup?partner=:slug
    path: '/register/:slug?',
    redirect: (to: any) => ({
      path: '/signup',
      query: to.params.slug ? { partner: to.params.slug } : {},
    }),
  },
  {
    // Landing page of the emailed one-time login for a Datev Accountant.
    path: '/magic-login',
    component: () => import('@/pages/MagicLogin.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/forgot-password',
    component: () => import('@/pages/ForgotPassword.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/reset-password',
    component: () => import('@/pages/ResetPassword.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: Dashboard,
      },
      {
        path: 'restaurants',
        name: 'Restaurants',
        component: Restaurants,
        meta: { requiredPermissions: ['restaurant:list'] },
      },
      {
        path: 'restaurants/:id',
        name: 'RestaurantDetail',
        component: () => import('@/pages/RestaurantDetail.vue'),
        meta: { requiredPermissions: ['restaurant:list'] },
      },
      {
        path: 'roles-permissions',
        name: 'RolesPermissions',
        component: () => import('@/pages/RolesPermissions.vue'),
        meta: { requiredPermissions: ['role:list'] },
      },
      {
        path: 'roles-permissions/:id/edit',
        name: 'EditRole',
        component: () => import('@/pages/EditRole.vue'),
        meta: { requiredPermissions: ['role:edit'] },
      },
      {
        path: 'restaurants/:id/pickup-monitor',
        name: 'PickupMonitorAdmin',
        component: () => import('@/pages/pum/PickupMonitorAdmin.vue'),
        meta: { allowedRoles: ['pickup_monitor'] },
      },
      {
        path: 'partners',
        name: 'Partners',
        component: () => import('@/pages/Partners.vue'),
        meta: { requiredPermissions: ['partners:list'] },
      },
      {
        path: 'companies',
        name: 'Companies',
        component: () => import('@/pages/Companies.vue'),
        meta: { requiredPermissions: ['companies:list'] },
      },
      {
        /*
         * Settings. DATEV is the only section today; further ones are a child
         * route plus an entry in SettingsLayout's sidebarItems.
         */
        path: 'settings',
        component: () => import('@/pages/settings/SettingsLayout.vue'),
        children: [
          {
            path: '',
            redirect: () => {
              const authStore = useAuthStore()
              return authStore.user?.role === 'restaurant_company' || authStore.user?.role === 'partner'
                ? { name: 'SettingsCompanyDetails' }
                : { name: 'SettingsDatev' }
            },
          },
          {
            path: 'datev',
            name: 'SettingsDatev',
            component: () => import('@/pages/settings/Datev.vue'),
            meta: { requiredPermissions: ['company-datev:view'] },
          },
          {
            path: 'company-details',
            name: 'SettingsCompanyDetails',
            component: () => import('@/pages/settings/CompanyDetails.vue'),
            meta: { allowedRoles: ['restaurant_company', 'partner'] },
          },
          {
            path: 'tax-details',
            name: 'SettingsTaxDetails',
            component: () => import('@/pages/settings/TaxDetails.vue'),
            meta: { allowedRoles: ['restaurant_company'] },
          },
          {
            // Admin-only: bulk-toggle restaurant_details.use_sandbox_mode across restaurants.
            path: 'sandbox-mode',
            name: 'SettingsSandboxMode',
            component: () => import('@/pages/settings/SandboxMode.vue'),
            meta: { allowedRoles: ['super_admin'] },
          },
        ],
      },
      {
        /*
         * Where the API's DATEV callback sends the browser once it has exchanged
         * the authorization code. Inside the authenticated shell: the admin is
         * already signed in, they only left for DATEV's login.
         */
        path: 'datev/callback',
        name: 'DatevCallback',
        component: () => import('@/pages/DatevCallback.vue'),
        // Nothing to land from where DATEV online is not approved.
        beforeEnter: () => (config.features.datevOnline ? true : { path: '/' }),
      },
      {
        path: 'users',
        name: 'Users',
        component: () => import('@/pages/Users.vue'),
        meta: { requiredPermissions: ['users:list'] },
      },
      {
        path: 'licenses',
        component: () => import('@/pages/licenses/LicensesLayout.vue'),
        meta: { requiredPermissions: ['licenses:list'] },
        children: [
          {
            path: '',
            redirect: { name: 'LicensesSubscriptions' },
          },
          {
            path: 'success',
            name: 'LicensesCheckoutSuccess',
            component: () => import('@/pages/licenses/CheckoutSuccess.vue'),
          },
          {
            path: 'cancel',
            name: 'LicensesCheckoutCancel',
            component: () => import('@/pages/licenses/CheckoutCancel.vue'),
          },
          {
            path: 'subscriptions',
            name: 'LicensesSubscriptions',
            component: () => import('@/pages/licenses/Subscriptions.vue'),
          },
          {
            path: 'payment-methods',
            name: 'LicensesPaymentMethods',
            component: () => import('@/pages/licenses/PaymentMethods.vue'),
          },
          {
            path: 'billing-details',
            name: 'LicensesBillingDetails',
            component: () => import('@/pages/licenses/BillingDetails.vue'),
          },
          {
            path: 'reports',
            name: 'LicensesReports',
            component: () => import('@/pages/licenses/Reports.vue'),
            meta: { allowedRoles: ['super_admin'] },
          },
          {
            path: 'invoice-prepayment',
            name: 'LicensesInvoicePrepayment',
            component: () => import('@/pages/licenses/InvoicePrepayment.vue'),
            meta: { allowedRoles: ['super_admin'] },
          },
        ],
      },
    ],
  },
  {
    path: '/onboarding/:step',
    name: 'Onboarding',
    component: () => import('@/pages/onboarding/OnboardingLayout.vue'),
    meta: { requiresAuth: true, requiredPermissions: ['restaurant:create'] },
  },
  {
    path: '/restaurants/edit/:id/:step',
    name: 'EditRestaurant',
    component: () => import('@/pages/onboarding/OnboardingLayout.vue'),
    meta: { requiresAuth: true, requiredPermissions: ['restaurant:create'] },
  },
  {
    path: '/logs/:restaurantId/:logPath',
    name: 'Logs',
    component: () => import('@/pages/Logs.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/pum/login/:restaurantSlug/:userEmail',
    name: 'PUMAutoLogin',
    component: () => import('@/pages/pum/AutoLogin.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/pum/:slug/:accountName',
    name: 'PickupMonitorList',
    component: () => import('@/pages/pum/PickupMonitorList.vue'),
    meta: { requiresAuth: false },
  },
  {
    // Old permanent, reusable reception auto-login link — deprecated in favor of the
    // short-lived /rcp/login?token=... flow. Bounce anyone who still has the old URL
    // (bookmarked, printed, shared) to the normal rcp/terminal instead of logging them in.
    path: '/rcp/login/:restaurantSlug/:userEmail',
    redirect: '/rcp/terminal',
  },
  {
    path: '/rcp/login',
    name: 'RcpTokenLogin',
    component: () => import('@/pages/rcp/TokenLogin.vue'),
    meta: { requiresAuth: false },
  },
  {
    // In-app QR scanner — lets a reception device log in by scanning the admin's QR
    // from inside the (installed) app itself, so the session lands in the app's own
    // storage rather than being redirected out to Safari.
    path: '/rcp/scan',
    name: 'RcpScanLogin',
    component: () => import('@/pages/rcp/ScanLogin.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/rcp/terminal',
    name: 'RcpTerminal',
    component: () => import('@/pages/rcp/ReceptionTerminal.vue'),
    meta: { requiresAuth: true, allowedRoles: ['reception'] },
  },
  {
    path: '/rcp/terminal/:slug/:email',
    redirect: '/rcp/terminal',
  },
  // Dev-only gallery of the shared form primitives. Guarded by import.meta.env.DEV
  // so it is tree-shaken out of production builds along with the page itself.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/design-system',
          name: 'DesignSystem',
          component: () => import('@/pages/DesignSystem.vue'),
          meta: { requiresAuth: false },
        },
      ]
    : []),
  {
    path: '/:pathMatch(.*)*',
    component: () => import('@/pages/NotFound.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach((to, from, next) => {
  try {
    // Admin and reception sessions are stored under separate localStorage keys
    // (see src/stores/auth.ts) so both can be logged in independently in the same
    // browser. Which pair applies is decided by the target route's "zone" — /rcp/*
    // is always a reception route (role-gated below), everything else is admin.
    const isRcpRoute = to.path.startsWith('/rcp/')
    const isAuthenticated = localStorage.getItem(isRcpRoute ? 'rcpIsAuthenticated' : 'isAuthenticated') === 'true'
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches

    if (to.meta.requiresAuth && !isAuthenticated && to.path !== '/login') {
      next('/login')
      return
    }

    if ((to.path === '/login' || to.path === '/signup') && isAuthenticated) {
      next('/')
      return
    }

    // Check user role for role-based redirection
    const userJsonString = localStorage.getItem(isRcpRoute ? 'rcpUser' : 'user')
    const user = userJsonString ? JSON.parse(userJsonString) : null

    // If user is authenticated and has pickup_monitor role, redirect to pickup monitor page
    if (isAuthenticated && user && user.role === 'pickup_monitor') {
      // Only allow access to pickup monitor routes
      if (to.path.includes('/pickup-monitor') || to.path === '/login') {
        next()
        return
      }

      // Redirect all other routes to the pickup monitor dashboard
      // Use the restaurant ID from user profile
      const restaurantId = user.restaurantId || '1'
      next(`/restaurants/${restaurantId}/pickup-monitor`)
      return
    }

    // A Datev Accountant works in the DATEV settings (where the DATEV export is) and
    // nowhere else. The API refuses the rest anyway; this
    // keeps them from landing on screens full of failed requests.
    // Only while the role still carries the DATEV permission: without it /settings/datev
    // sends them back to '/', which would send them here again.
    if (
      isAuthenticated &&
      !isRcpRoute &&
      user &&
      isDatevAccountant(user) &&
      hasPermission(user, 'company-datev:view')
    ) {
      const allowed =
        to.path === '/settings' ||
        to.path.startsWith('/settings/datev') ||
        to.path === '/datev/callback' ||
        to.path === '/magic-login'

      if (!allowed) {
        next('/settings/datev')
        return
      }
    }

    // Check role restrictions - only if authenticated
    const allowedRoles = to.meta.allowedRoles as string[] | undefined
    if (allowedRoles && allowedRoles.length > 0 && isAuthenticated && user) {
      if (!allowedRoles.includes(user.role)) {
        console.warn(`Access denied to ${to.path}: User role '${user.role}' not in allowed roles`, allowedRoles)
        next(isStandalone ? '/rcp/terminal' : '/')
        return
      }
    }

    // Check permissions - only if authenticated and permissions are required
    const requiredPermissions = to.meta.requiredPermissions as string[] | undefined
    if (requiredPermissions && requiredPermissions.length > 0 && isAuthenticated) {
      try {
        // If user data isn't loaded yet, allow navigation and let the page handle it
        if (!user) {
          console.warn(`User data not loaded yet for route ${to.path}`)
          next()
          return
        }

        // Check if user has required permissions
        // hasPermission() includes admin bypass: admins automatically have all permissions
        const hasRequiredPermissions = requiredPermissions.some((permission) =>
          hasPermission(user, permission)
        )

        if (!hasRequiredPermissions) {
          console.warn(`Access denied to ${to.path}: Missing required permissions`, requiredPermissions)
          next(isStandalone ? '/rcp/terminal' : '/')
          return
        }
      } catch (permissionError) {
        console.warn(`Error checking permissions for route ${to.path}:`, permissionError)
        // Allow navigation on error to prevent complete blocking
        next()
        return
      }
    }

    // Check onboarding step access - prevent jumping ahead in onboarding flow
    if ((to.path.includes('/onboarding/') || to.path.includes('/restaurants/edit/')) && to.params.step) {
      try {
        const restaurantInfoStore = useRestaurantInfoStore()
        const stepParam = to.params.step as string

        // Map step name to step number
        const stepMap: Record<string, number> = {
          'basic-information': 1,
          'opening-hours': 2,
          'payment-gateway': 3,
          'matrix-interface': 4,
          'restaurant-settings': 5,
        }

        const stepNumber = stepMap[stepParam]

        if (stepNumber && !restaurantInfoStore.isStepAccessible(stepNumber)) {
          console.warn(`Access denied to step ${stepNumber}: Step ${stepNumber - 1} must be completed first`)
          // Redirect to the first accessible step
          const firstAccessibleStep = restaurantInfoStore.getAccessibleSteps()[0] || 1
          const stepRoutes = ['basic-information', 'opening-hours', 'payment-gateway', 'matrix-interface', 'restaurant-settings']
          const isEditMode = to.path.includes('/restaurants/edit/')
          const basePath = isEditMode ? `/restaurants/edit/${to.params.id}` : '/onboarding'
          next(`${basePath}/${stepRoutes[firstAccessibleStep - 1]}`)
          return
        }
      } catch (stepError) {
        console.warn(`Error checking onboarding step access for route ${to.path}:`, stepError)
        // Allow navigation on error to prevent complete blocking
        next()
        return
      }
    }

    next()
  } catch (error) {
    console.error('Router guard error:', error)
    next()
  }
})

// Handle dynamic import (chunk) failures — usually caused by stale cache after a deployment
installChunkReload(router)

export default router
